import {
  AsyncSubject,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  merge,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  Subscription,
  timeout,
} from 'rxjs';
import {v4 as uuid} from 'uuid';
import {debounce, defer, isEqual, omit, reduce} from 'lodash';
import {
  Attachment,
  AttachmentConversationMessage,
  BaseConversationMessage,
  Client,
  ClientResponse,
  ConversationEvent,
  ConversationEventType,
  ConversationMessage,
  ConversationMessageStatus,
  ConversationParticipant,
  ConversationStatus,
  TextConversationMessage,
  ConversationMessageError,
  ConversationSyncStorageMode,
  RequestACallParams,
  SubmitEmailIssueParams,
  ConversationChannel,
  ConversationAction,
  SelectOptionAction,
  RichTextConversationMessage,
  ButtonOptionsConversationMessage,
  SelectOptionsConversationMessage,
  TriggerParams,
  RequestAction,
  ConversationRequestAction,
  RequestACallDetails,
  InlineDataConversationMessage,
  StripeAssistantLiveAnswersResponse,
  SendFeedbackParams,
} from './types';
import {ACCEPTED_MIME_TYPES, MAX_FILE_SIZE} from './helpers';
import {
  buildLiveAnswersClient,
  LiveAnswersClient,
  LiveAnswersMessage,
} from './live-answers-client';
import {ConversationLog, ConversationLogItem} from './conversation-log';
import {
  clearStorage,
  loadStateFromStorage,
  StoredState,
  syncStorage,
} from './storage';
import {METRIC} from './metrics';
import {ErrorScope} from './observability';

export const TAB_ID = uuid();

export type ConversationTab = {
  id: string;
  interacted?: boolean;
};

const STORAGE_PREFIX = 'twilio_chat';
export const CONVERSATION_STORAGE_PREFIX = `${STORAGE_PREFIX}_conversation`;

export const REQUEST_ACTION_DEFAULT_TIMEOUT_MS = 500;

export interface ConversationContext extends Record<string, unknown> {
  topic?: string;
  audience?: string;
  entryPoint?: string;
  channel?: ConversationChannel;
  clientType?: string;
  soundSetting?: string;
  engagedTabs?: string[];
  activeTabs?: string[];
  tabs?: ConversationTab[];
  csatToken?: string;
  disableCleanupOnEnd?: boolean;
  triggerParams?: TriggerParams;
  requestACallDetails?: RequestACallDetails;
  supressChatEndedNotice?: boolean;
  restartConversation?: {
    initialQuery: string;
  };
  feedback_v2_enabled?: boolean; // this should eventually be set from api response
}

export type StoredConversationState = {
  status: ConversationStatus;
  messages: ConversationMessage[];
  participants: ConversationParticipant[];
  log: ConversationLogItem[];
  context?: ConversationContext;
};

type ConversationParams = {
  id: string;
  token?: string;
  user: ConversationParticipant;
  messages?: Record<string, ConversationMessage>;
  participants?: Record<string, ConversationParticipant>;
  status?: ConversationStatus;
  syncStorage?: ConversationSyncStorageMode;
  context?: ConversationContext;
};

export class Conversation {
  protected _id: string;

  protected _client: Client;

  protected _token?: string;

  protected _user: ConversationParticipant;

  protected _log: ConversationLog;

  protected status$: BehaviorSubject<ConversationStatus>;

  protected messages$: BehaviorSubject<Record<string, ConversationMessage>>;

  protected participants$: BehaviorSubject<
    Record<string, ConversationParticipant>
  >;

  protected listeners: Set<(event: ConversationEvent) => void> = new Set();

  protected events$: Subject<ConversationEvent> = new Subject();

  protected debouncedTypingEnded: Record<string, () => void> = {};

  protected typers$: BehaviorSubject<Record<string, ConversationParticipant>> =
    new BehaviorSubject({});

  private context$: BehaviorSubject<ConversationContext> = new BehaviorSubject(
    {},
  );

  private tabEventsRegistered = false;

  protected sub: Subscription | null = null;

  protected syncStorageMode: ConversationSyncStorageMode;

  storageVersion = 0;

  constructor(client: Client, params: ConversationParams) {
    this._id = params.id;
    this._client = client;
    this._token = params.token;
    this._user = params.user;
    this.syncStorageMode = params.syncStorage;

    const status = params.status || ConversationStatus.Active;
    const messages: Record<string, ConversationMessage> = params.messages || {};
    const participants: Record<string, ConversationParticipant> =
      params.participants || {
        [this.user.id]: this.user,
      };
    const context: ConversationContext = {
      ...params.context,
      clientType: this.client.type,
      channel: this.client.channel,
    };
    const channelIdKey = `${this.client.channel}_conversationId`;
    context[channelIdKey] = this.id;

    this.status$ = new BehaviorSubject(status);
    this.messages$ = new BehaviorSubject(messages);
    this.participants$ = new BehaviorSubject(participants);
    this.context$ = new BehaviorSubject(context);

    this._log = new ConversationLog(this);

    if (this.syncStorageMode) {
      const storedState = this.loadStoredState();
      if (storedState) {
        this.setStateFromStorage(storedState);
      }
    }
    this.eventsObservable().subscribe((event) => {
      this.listeners.forEach((listener) => listener(event));
    });

    merge(this.status$, this.participants$).subscribe(() => {
      if (this.hasEnded) {
        // when the conversation becomes inactive and there are no other participants
        // emit an event to indicate that the conversation has ended
        this.emitNewEvent({
          conversationId: this.id,
          name: 'conversation-ended',
        });
      }
    });

    this.registerTabFocusListeners();
  }

  get tabId(): string {
    return TAB_ID;
  }

  get tabs(): ConversationTab[] {
    return this.context.tabs ?? [];
  }

  isTabInteracted(): boolean {
    const {tabs} = this;
    return tabs.some((t) => t.id === this.tabId && t.interacted);
  }

  isTabMostRecent(): boolean {
    return this.mostRecentTab().id === this.tabId;
  }

  isTabMostRecentlyInteracted(): boolean {
    return this.mostRecentlyInteractedTab()?.id === this.tabId;
  }

  mostRecentTab(): ConversationTab {
    const {tabs} = this;
    return tabs[0] ?? {id: this.tabId};
  }

  mostRecentlyInteractedTab(): ConversationTab | undefined {
    const {tabs} = this;
    return tabs.find((t) => !!t.interacted);
  }

  get isFeedbackV2Enabled(): boolean {
    // we "enable" feedback v2 from the server by adding a `feedback` key
    // to the effect objects, which has a value and action.
    // /api/universal-help/response-feedback
    return Object.values(this.messages).some(
      (m: ConversationMessage) => m.feedback !== undefined,
    );
  }

  get hasEnded(): boolean {
    // this logic came from how the Twilio client logic worked.
    // we should revisit the conversation lifecycle with a holistic approach
    // of how it should work across all clients
    const participants = Object.values(this.participants).filter(
      (p) => p.role !== 'bot',
    );
    return (
      this.status === ConversationStatus.Inactive && participants.length < 3
    );
  }

  get id(): string {
    return this._id;
  }

  get client(): Client {
    return this._client;
  }

  get token(): string | undefined {
    return this._token;
  }

  get user(): ConversationParticipant {
    return this._user;
  }

  get log(): ConversationLog {
    return this._log;
  }

  get status(): ConversationStatus {
    return this.status$.getValue();
  }

  get messages(): Record<string, ConversationMessage> {
    return this.messages$.getValue();
  }

  get participants(): Record<string, ConversationParticipant> {
    return this.participants$.getValue();
  }

  get isActive(): boolean {
    return this.status === ConversationStatus.Active;
  }

  // userIsActive is a convenience method around whether the
  // user should be able to interact with this conversation anymore
  // based on whether the conversation is active and whether the
  // user is a participant in the conversation anymore
  get userIsActive(): boolean {
    const userIsParticipating = !!this.participants[this.user.id];
    return this.isActive && userIsParticipating;
  }

  get context(): ConversationContext {
    return this.context$.getValue();
  }

  setContextValue(key: string, value: unknown): void {
    this.storageVersion += 1;
    this.context$.next({...this.context, [key]: value});
  }

  setContextValues(values: ConversationContext): void {
    this.storageVersion += 1;
    this.context$.next({...this.context, ...values});
  }

  clearContextValues(keys: string[]): void {
    this.storageVersion += 1;
    this.context$.next(omit({...this.context}, keys));
  }

  getMessage(id: string): ConversationMessage | null {
    const messages = this.messages$.getValue();
    return messages[id] || null;
  }

  mostRecentMessage(): ConversationMessage | null {
    const log = this.log.get();

    if (!log || log.length === 0) {
      return null;
    }

    const item = log[log.length - 1];
    if (item.name === 'conversation-message-added') {
      return item.message;
    }

    return null;
  }

  getParticipant(id: string): ConversationParticipant | null {
    const participants = this.participants$.getValue();
    return participants[id] || null;
  }

  protected newMessage(): BaseConversationMessage {
    const nonce = uuid();
    return {
      id: nonce,
      participant: this.user,
      timestamp: Date.now(),
      status: ConversationMessageStatus.Pending,
      nonce,
    };
  }

  buildAttachmentMessage = (file: File): AttachmentConversationMessage => {
    const attachment: Attachment = {
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    };

    return {
      ...this.newMessage(),
      attachment,
      type: 'attachment-conversation-message',
    };
  };

  buildTextMessage = (text: string): TextConversationMessage => {
    return {
      ...this.newMessage(),
      text,
      type: 'text-conversation-message',
    };
  };

  validateMessage = (
    message: ConversationMessage,
  ): ConversationMessageError[] => {
    const validationErrors: ConversationMessageError[] = [];

    if (
      message.type === 'attachment-conversation-message' &&
      message.attachment.file
    ) {
      if (message.attachment.file.size > MAX_FILE_SIZE) {
        validationErrors.push(ConversationMessageError.AttachmentTooLargeError);
      }

      if (
        !Object.keys(ACCEPTED_MIME_TYPES).includes(message.attachment.file.type)
      ) {
        validationErrors.push(ConversationMessageError.AttachmentTypeError);
      }
    }

    return validationErrors;
  };

  async sendMessage(message: ConversationMessage): Promise<ClientResponse> {
    const messages = this.messages$.getValue();
    let next = {...messages};
    next[message.id] = message;
    this.storageVersion += 1;
    this.messages$.next(next);

    this.emitNewEvent({
      name: 'conversation-message-added',
      conversationId: this.id,
      participantId: message.participant.id,
      messageId: message.id,
      nonce: message.nonce,
      message,
    });

    let status: ConversationMessageStatus =
      message.status || ConversationMessageStatus.Pending;
    let validationErrors: ConversationMessageError[] | undefined;

    let response: ClientResponse;
    try {
      validationErrors = this.validateMessage(message);

      if (validationErrors.length) {
        status = ConversationMessageStatus.Error;
        response = {code: 400, error: validationErrors.join(', ')};
      } else {
        response = await this.client.sendConversationMessage({
          token: this.token,
          conversationId: this.id,
          message,
        });
      }

      // for placeholder conversations, we'll return a 205 status code
      if (response.code !== 200 && response.code !== 205) {
        status = ConversationMessageStatus.Error;
        if (response.code !== 408) {
          this.incrementAndTrack(METRIC.send_message_error, {
            messageType: message.type,
            code: response.code,
          });
        }
      } else {
        this.incrementAndTrack(METRIC.send_message_success, {
          messageType: message.type,
        });
      }
    } catch (e) {
      status = ConversationMessageStatus.Error;

      const error = (e as Error).message;
      response = {code: 500, error};

      this.reportAndEmitConversationException(
        `exception sending conversation message: ${error}`,
        {
          messageType: message.type,
          clientType: this.client.type,
        },
      );

      this.incrementAndTrack(METRIC.send_message_error, {
        messageType: message.type,
        code: response.code,
      });
    }

    if (status === ConversationMessageStatus.Error) {
      next = {...this.messages};
      next[message.id] = {
        ...message,
        status,
        ...(validationErrors?.length ? {errors: validationErrors} : {}),
      };

      this.storageVersion += 1;
      this.messages$.next(next);

      this.emitNewEvent({
        name: 'conversation-message-updated',
        conversationId: this.id,
        participantId: message.participant.id,
        messageId: message.id,
        nonce: message.nonce,
      });
    }

    this.emitNewEvent({
      name: 'conversation-message-updated',
      conversationId: this.id,
      participantId: message.participant.id,
      messageId: message.id,
      nonce: message.nonce,
    });

    return response;
  }

  // It's a smell this method knows about Live Answers and how it's shimmed into
  // Inline Data messages. It's better than having callers need to do that though.
  // Hopefully we'll revisit the API soon and make streaming messages a first
  // class citizen. In the meantime, this is a temporary measure to provide a
  // consistent way to handle Live Answers messages via the Conversation,
  // without exposing the underlying client directly and with callbacks that handle
  // the Inline Data result when streaming is done or if an error occurs.
  streamLiveAnswersMessage(message: InlineDataConversationMessage): {
    liveAnswersMessage: LiveAnswersMessage;
    liveAnswersClient: LiveAnswersClient;
    onDoneStreaming: (content: string) => void;
    onError: (error?: string) => void;
  } {
    const {liveAnswersClient, liveAnswersMessage} = buildLiveAnswersClient({
      client: this.client,
      conversationId: this.id,
      message,
    });

    const onError = (error?: string) => {
      if (!this.mostRecentTab()) {
        return;
      }

      this.action({
        name: 'inline-data-result',
        message,
        result: {type: 'error'},
      });

      this.client.errors.reportError(
        `Error streaming live answers message: ${error}`,
        {
          extras: {
            conversationId: this.id,
            messageId: message.id,
          },
        },
      );
    };

    const onDoneStreaming = (
      content: string,
      answerType: 'unanswerable' | 'answerable' = 'answerable',
    ) => {
      if (!this.mostRecentTab()) {
        return;
      }

      // Update the message. If an error happened, the message will the saved with
      // `isDone` as true but without any content. This is consistent with how it
      // currently renders: it will render nothing and the backend has sent a new
      // message to the user about the error which is rendered separately
      const updatedData = {
        ...(message.inlineData.data as StripeAssistantLiveAnswersResponse),
        is_streamed: true,
        content,
      };
      const updatedMessage = {
        ...message,
        inlineData: {...message.inlineData, data: updatedData},
      };

      this.action({
        name: 'inline-data-result',
        message: updatedMessage,
        result: {type: 'success', data: {outcome: answerType}},
      });
    };

    return {
      liveAnswersClient,
      liveAnswersMessage,
      onDoneStreaming,
      onError,
    };
  }

  async leave(): Promise<ClientResponse> {
    if (!this.userIsActive) {
      return Promise.resolve({code: 200});
    }

    this.storageVersion += 1;
    this.status$.next(ConversationStatus.Inactive);

    const resp = await this.client.leaveConversation({
      token: this.token,
      conversationId: this.id,
      participant: this.user,
    });

    // if we've left the conversation, we should disconnect
    // as we don't need (or probably want) to listen any
    // subsequent events
    defer(() => {
      if (!this.context.disableCleanupOnEnd) {
        this.disconnect();
        this.clearStorage();
      }
    });

    return resp;
  }

  sendTyping(): void {
    try {
      this.client.sendConversationTyping({
        token: this.token,
        conversationId: this.id,
        participantId: this.user.id,
      });
    } catch (e) {
      /* ignore typing event errors */
    }
  }

  clearTypers(): void {
    this.typers$.next({});
  }

  private overrideCachedMessagesFeedback(
    responseId: string,
    value: 'thumbs_up' | 'thumbs_down' | 'none' | undefined,
  ) {
    const updates: Record<string, ConversationMessage> = {};

    Object.values(this.messages).forEach((msg) => {
      if (msg.responseId === responseId) {
        updates[msg.nonce || msg.id] = {
          ...msg,
          feedback: {
            value,
            action: msg.feedback?.action ?? 'log_only',
          },
        } as ConversationMessage;
      }
    });

    if (Object.keys(updates).length) {
      this.updateMessages(updates);
    }
  }

  private get mostRecentBotMessage(): ConversationMessage | undefined {
    return Object.values(this.messages)
      .filter((msg) => msg.participant.role === 'bot')
      .sort((a, b) => a.timestamp - b.timestamp)
      .at(-1);
  }

  private shouldFeedbackAdvanceConversation(
    feedbackValue: SendFeedbackParams['feedback'],
    responseId: string,
  ): boolean {
    if (feedbackValue === 'none') {
      return false;
    }

    const message = this.mostRecentBotMessage;
    if (message?.responseId !== responseId) {
      return false;
    }

    return message?.feedback?.action === `${feedbackValue}_advance`;
  }

  async sendFeedback({
    responseId,
    feedback,
  }: Omit<SendFeedbackParams, 'conversationId'>): Promise<void> {
    const promises: Promise<unknown>[] = [];

    const shouldAdvance = this.shouldFeedbackAdvanceConversation(
      feedback,
      responseId,
    );

    if (shouldAdvance) {
      this.overrideCachedMessagesFeedback(responseId, feedback);

      promises.push(
        this.client.action({
          conversationId: this.id,
          action: {name: 'thumbs-down-advance'},
        }),
      );
    }

    const sendFeedbackPromise = this.client
      .sendFeedback({feedback, responseId, conversationId: this.id})
      .then((r) => {
        // Only update UI if we haven't already done so above
        if (!r.error && !shouldAdvance) {
          this.overrideCachedMessagesFeedback(responseId, feedback);
        }
      });

    promises.push(sendFeedbackPromise);

    await Promise.allSettled(promises);
  }

  async selectOption(
    action: Omit<SelectOptionAction, 'name'>,
  ): Promise<ClientResponse> {
    const {message: optionMessage, selectedOptionId: optionId} = action;

    // if the option message was embedded, the id will be for the parent message
    const message = this.getMessage(optionMessage.id);

    const updateMessage = (
      status: ConversationMessageStatus,
      // if we pass selectedOptionId as undefined, it will unset it (used in error case)
      selectedOptionId?: string,
    ) => {
      if (!message) {
        return;
      }

      let updated:
        | RichTextConversationMessage
        | ButtonOptionsConversationMessage
        | SelectOptionsConversationMessage;

      if (message.type === 'rich-text-conversation-message') {
        const {data} = message;
        updated = {
          ...message,
          data: {
            ...data,
            ...optionMessage,
            selectedOptionId,
          },
          status,
        };
      } else {
        updated = {
          ...message,
          selectedOptionId,
          status,
        } as
          | ButtonOptionsConversationMessage
          | SelectOptionsConversationMessage;
      }

      this.updateMessages({[updated.id]: updated});
    };

    updateMessage(ConversationMessageStatus.Pending, optionId);

    let response: ClientResponse;

    try {
      response = await this.action({
        ...action,
        name: 'select-option',
      });

      if (response.code === 200) {
        updateMessage(ConversationMessageStatus.Sent, optionId);
      } else {
        updateMessage(ConversationMessageStatus.Error);
      }
    } catch (e) {
      // we report this error in action
      response = {code: 500, error: (e as Error).message};

      updateMessage(ConversationMessageStatus.Error);
    }

    return response;
  }

  async action(action: ConversationAction): Promise<ClientResponse> {
    if (action.name === 'request-action') {
      return this.requestAction(action as RequestAction);
    }

    let response: ClientResponse;
    try {
      response = await this.client.action({
        token: this.token,
        conversationId: this.id,
        action,
      });

      if (response.code >= 400) {
        if (response.code !== 408) {
          this.incrementAndTrack(METRIC.action_error, {
            actionName: action.name as string,
            code: response.code,
          });
        }
      } else {
        this.incrementAndTrack(METRIC.action_success, {
          actionName: action.name as string,
        });
      }
    } catch (e) {
      const error = (e as Error).message;
      response = {code: 500, error};

      this.reportAndEmitConversationException(
        `exception sending conversation action: ${error}`,
      );

      this.incrementAndTrack(METRIC.action_error, {
        actionName: action.name as string,
        code: response.code,
      });
    }

    return response;
  }

  triggerScrollToTop(): void {
    this.emitNewEvent({
      name: 'conversation-trigger-scroll-to-top',
      conversationId: this.id,
    });
  }

  protected async requestAction(
    requestAction: RequestAction,
  ): Promise<ClientResponse> {
    const {action} = requestAction;

    const event: ConversationRequestAction = {
      conversationId: this.id,
      name: 'conversation-request-action',
      action,
    };

    // This behavior could also be modeled as Promises; AsyncSubject behaves
    // similarly though, but provides a more imperative API.
    // AsyncSubject will emit the last (and only the last) value emitted
    // after complete is called (complete *must* be called).
    const responseSubject = new AsyncSubject<ClientResponse>();
    const responseTimeout =
      requestAction.responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS;

    // this callback request action handlers can use to provide a response
    const respondWith = (data: Record<string, unknown> | undefined) => {
      responseSubject.next({code: 200, data});
      responseSubject.complete();
    };

    if (requestAction.waitForResponse) {
      event.respondWith = respondWith;
      event.responseTimeout = responseTimeout;
    } else {
      // if we don't need to wait for a response, respond immediately with no data
      // this will complete the AsyncSubject immediately
      respondWith(undefined);
    }

    const timeoutResponse = {
      code: 400,
      error: `Timeout waiting for request-action response for ${action.name}`,
    };

    // the result is the first of either our response or the timeout response
    const result = firstValueFrom(
      responseSubject.pipe(
        timeout({
          each: responseTimeout,
          with: () => of(timeoutResponse),
        }),
      ),
    );

    this.emitNewEvent(event);

    return result;
  }

  async requestACall(
    params: Omit<RequestACallParams, 'conversationId'>,
  ): Promise<ClientResponse> {
    const racParams = {
      ...params,
      conversationId: this.id,
    } as RequestACallParams;
    return this.client.requestACall(racParams);
  }

  async getVerificationCodes(): Promise<ClientResponse> {
    return this.client.getVerificationCodes({
      conversationId: this.id,
    });
  }

  async submitEmailIssue(
    params: Omit<SubmitEmailIssueParams, 'conversationId'>,
  ): Promise<ClientResponse> {
    const emailParams = {
      ...params,
      conversationId: this.id,
    } as SubmitEmailIssueParams;
    return this.client.submitEmailIssue(emailParams);
  }

  async connect({fetch = true}: {fetch?: boolean} = {}): Promise<void> {
    this.sub?.unsubscribe();
    this.sub = new Subscription();

    const listener = (e: ConversationEvent) => this.handleEventFromClient(e);
    this.client.addConversationListener(this.id, listener);
    this.sub.add(() =>
      this.client.removeConversationListener(this.id, listener),
    );

    if (this.syncStorageMode) {
      this.sub.add(this.syncStorage());
    }

    if (fetch) {
      await this.fetchConversation();
    }
  }

  async disconnect(): Promise<void> {
    this.clearTypers();
    this.sub?.unsubscribe();
  }

  addListener(listener: (event: ConversationEvent) => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: (event: ConversationEvent) => void): void {
    this.listeners.delete(listener);
  }

  eventsObservable(): Observable<ConversationEvent> {
    return this.events$.asObservable();
  }

  statusObservable(): Observable<ConversationStatus> {
    return this.status$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  }

  messagesObservable(): Observable<Record<string, ConversationMessage>> {
    return this.messages$.asObservable().pipe(
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  }

  participantsObservable(): Observable<
    Record<string, ConversationParticipant>
  > {
    return this.participants$.asObservable().pipe(
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  }

  typingObservable(): Observable<ConversationParticipant[]> {
    return this.typers$.asObservable().pipe(
      // skip(1) skips initial value; only emit changes
      skip(1),
      map(Object.values),
    );
  }

  contextObservable(): Observable<ConversationContext> {
    return this.context$.asObservable().pipe(skip(1));
  }

  handleEventFromClient = async (event: ConversationEvent): Promise<void> => {
    if (event.conversationId !== this.id) {
      return;
    }

    switch (event.name) {
      case 'conversation-updated': {
        if (this.status !== event.status) {
          this.storageVersion += 1;
          this.status$.next(event.status);
          this.emit(event);
        }
        break;
      }
      case 'conversation-participant-left': {
        if (this.typers$.getValue()[event.participant.id]) {
          this.emitEndTyping(event.participant.id);
        }
        delete this.debouncedTypingEnded[event.participant.id];

        const participants = {...this.participants};
        if (participants[event.participant.id]) {
          delete participants[event.participant.id];
          this.storageVersion += 1;
          this.participants$.next(participants);
          this.emit(event);
        }
        break;
      }
      case 'conversation-participant-joined':
      case 'conversation-participant-updated': {
        const participants = {...this.participants};
        const existing = participants[event.participant.id];
        if (!existing) {
          participants[event.participant.id] = event.participant;
          this.storageVersion += 1;
          this.participants$.next(participants);
          this.emit({
            ...event,
            name: 'conversation-participant-joined',
          });
        } else if (!isEqual(event.participant, existing)) {
          participants[event.participant.id] = event.participant;
          this.storageVersion += 1;
          this.participants$.next(participants);
          this.emit({
            ...event,
            name: 'conversation-participant-updated',
          });
        }
        break;
      }
      case 'conversation-message-added':
      case 'conversation-message-updated': {
        if (event.participantId) {
          this.emitEndTyping(event.participantId);
        }

        if (event.message) {
          this.updateMessages({[event.message.id]: event.message});
        } else {
          this.fetchMessages([event.messageId]);
        }
        break;
      }
      case 'conversation-typing-started': {
        this.emitTyping(event.participantId);
        break;
      }
      case 'conversation-typing-ended': {
        this.emitEndTyping(event.participantId);
        break;
      }
      case 'conversation-set-context-values': {
        this.setContextValues(event.values);
        break;
      }
      case 'conversation-clear-context-values': {
        this.clearContextValues(event.keys);
        break;
      }
      case 'conversation-option-selected': {
        this.emit(event);
        break;
      }
      default:
    }
  };

  protected async fetchConversation(): Promise<void> {
    await this.fetchParticipants();
    await this.fetchMessages();
  }

  protected async fetchMessages(messageIds?: string[]): Promise<void> {
    try {
      const fetchStartMs = Date.now();
      const resp = await this.client.getConversationMessages({
        token: this.token,
        conversationId: this.id,
        messageIds,
      });
      this.client.metrics.timing(
        METRIC.fetch_messages_duration,
        Date.now() - fetchStartMs,
        {client_type: this.client.type},
      );
      if ('error' in resp) {
        this.emitNewEvent({
          name: 'conversation-error',
          conversationId: this.id,
          error: `error fetching messages: ${resp.error}`,
        });
        if (resp.code !== 408) {
          this.incrementAndTrack(METRIC.fetch_messages_error, {
            code: resp.code,
          });
        }

        return;
      }

      await this.updateMessages(resp.data.messages);
    } catch (e) {
      const error = (e as Error).message;

      this.reportAndEmitConversationException(
        `exception fetching messages: ${error}`,
      );

      this.incrementAndTrack(METRIC.fetch_messages_error);
    }
  }

  protected async updateMessages(
    messages: Record<string, ConversationMessage>,
  ): Promise<void> {
    try {
      const existingMessages = this.messages;
      const respMessages = Object.values(messages);

      const idToNonce = Object.values(existingMessages).reduce(
        (acc, message) => {
          acc[message.id] = message.nonce;
          return acc;
        },
        {} as Record<string, string | undefined>,
      );

      const updatedMessages: ConversationMessage[] = [];
      const newMessages: ConversationMessage[] = [];

      const next: Record<string, ConversationMessage> = {...existingMessages};
      respMessages.forEach((message) => {
        const nonce = message.nonce ?? idToNonce[message.id];
        let existing = existingMessages[message.id];
        if (!existing && nonce) {
          existing = existingMessages[nonce];
        }

        const messageWithNonce = {
          ...message,
          nonce,
        };

        if (!existing) {
          newMessages.push(messageWithNonce);
        } else if (!isEqual(messageWithNonce, existing)) {
          updatedMessages.push(messageWithNonce);
        }

        // to make local messages stable, prefer nonce if available
        next[nonce || message.id] = messageWithNonce;
      });

      this.storageVersion += 1;
      this.messages$.next(next);

      updatedMessages.forEach((message) => {
        this.emitNewEvent({
          name: 'conversation-message-updated',
          conversationId: this.id,
          participantId: message.participant.id,
          messageId: message.id,
          nonce: message.nonce,
          message,
        });
      });

      newMessages.forEach((message) => {
        this.emitNewEvent({
          name: 'conversation-message-added',
          conversationId: this.id,
          participantId: message.participant.id,
          messageId: message.id,
          nonce: message.nonce,
          message,
        });
      });
    } catch (e) {
      const error = (e as Error).message;

      this.reportAndEmitConversationException(
        `exception updating messages: ${error}`,
      );

      this.incrementAndTrack(METRIC.update_messages_error);
    }
  }

  protected async fetchParticipants(participantIds?: string[]): Promise<void> {
    try {
      const fetchStartMs = Date.now();
      const resp = await this.client.getConversationParticipants({
        token: this.token,
        conversationId: this.id,
        participantIds,
      });
      this.client.metrics.timing(
        METRIC.fetch_participants_duration,
        Date.now() - fetchStartMs,
        {client_type: this.client.type},
      );
      if ('error' in resp) {
        this.emitNewEvent({
          name: 'conversation-error',
          conversationId: this.id,
          error: `error fetching participants: ${resp.error}`,
        });
        if (resp.code !== 408) {
          this.incrementAndTrack(METRIC.fetch_participants_error, {
            code: resp.code,
          });
        }
        return;
      }

      const existingParticipants = this.participants;
      const respParticipants = Object.values(resp.data.participants);
      const updatedParticipants: ConversationParticipant[] = [];
      const newParticipants: ConversationParticipant[] = [];

      const next: Record<string, ConversationParticipant> = {
        ...existingParticipants,
      };
      respParticipants.forEach((participant) => {
        const existing = existingParticipants[participant.id];
        if (!existing) {
          newParticipants.push(participant);
        } else if (!isEqual(participant, existing)) {
          updatedParticipants.push(participant);
        }

        next[participant.id] = participant;
      });

      this.storageVersion += 1;
      this.participants$.next(next);

      updatedParticipants.forEach((participant) => {
        this.emitNewEvent({
          name: 'conversation-participant-updated',
          conversationId: this.id,
          participant,
        });
      });

      newParticipants.forEach((participant) => {
        this.emitNewEvent({
          name: 'conversation-participant-joined',
          conversationId: this.id,
          participant,
        });
      });
    } catch (e) {
      const error = (e as Error).message;

      this.reportAndEmitConversationException(
        `exception fetching participants: ${error}`,
      );

      this.incrementAndTrack(METRIC.fetch_participants_error);
    }
  }

  protected emitTyping(participantId: string): void {
    if (participantId === this.user.id) {
      return;
    }

    const participant = this.getParticipant(participantId);
    if (participant) {
      const next = {...this.typers$.getValue()};
      next[participant.id] = {...participant};
      this.typers$.next(next);
    }

    this.emitNewEvent({
      name: 'conversation-typing-started',
      conversationId: this.id,
      participantId,
    });

    this.emitDeferredEndTyping(participantId);
  }

  protected emitEndTyping(participantId: string): void {
    const next = {...this.typers$.getValue()};
    if (next[participantId]) {
      delete next[participantId];
      this.typers$.next(next);

      this.emitNewEvent({
        name: 'conversation-typing-ended',
        conversationId: this.id,
        participantId,
      });
    }
  }

  protected emitDeferredEndTyping(participantId: string): void {
    let emit = this.debouncedTypingEnded[participantId];
    if (!emit) {
      emit = debounce(() => this.emitEndTyping(participantId), 6000);
      this.debouncedTypingEnded[participantId] = emit;
    }
    emit();
  }

  protected emit(event: ConversationEvent): void {
    defer(() => this.events$.next(event));
  }

  protected emitNewEvent<T extends ConversationEventType>(event: T): void {
    this.emit(this.newEvent(event));
  }

  protected newEvent = <T extends ConversationEventType>(
    event: T,
  ): ConversationEvent => {
    return {
      id: uuid(),
      timestamp: Date.now(),
      ...event,
    };
  };

  storageKey = (): string => {
    return `${CONVERSATION_STORAGE_PREFIX}_${this.id}`;
  };

  loadStoredState = (): StoredState<StoredConversationState> | null => {
    return loadStateFromStorage<StoredConversationState>(this.storageKey());
  };

  static clearStorage({
    prefix = CONVERSATION_STORAGE_PREFIX,
    all = false,
  }: {prefix?: string; all?: boolean} = {}) {
    if (all) {
      clearStorage({prefix});
    } else {
      clearStorage({prefix, ttl: {value: 1, unit: 'hours'}});
    }
  }

  clearStorage = () => {
    Conversation.clearStorage({prefix: this.storageKey(), all: true});
  };

  protected storageStateObservable =
    (): Observable<StoredConversationState> => {
      // combine the latest values as they are emitted,
      // mapping it into StoredConversationState
      return combineLatest([
        this.status$.pipe(distinctUntilChanged()),
        this.messages$.pipe(map(Object.values)),
        this.participants$.pipe(map(Object.values)),
        this.log.observable().pipe(startWith(this.log.get())),
        this.context$,
      ]).pipe(
        map(([status, messages, participants, log, context]) => {
          return {
            status,
            messages,
            participants,
            log,
            context,
          };
        }),
      );
    };

  protected syncStorage = (): Subscription => {
    return syncStorage<StoredConversationState>({
      key: () => this.storageKey(),
      version: () => this.storageVersion,
      data: () => this.storageStateObservable(),
      nextFromStorage: (storedState: StoredState<StoredConversationState>) => {
        this.updateFromStorage(storedState);
      },
    });
  };

  // Sets conversation with provided version, timestamp, and any available storedState.data
  // Checked data include status, messages, participants, log, and context
  // The caller is responsible for checking the storage version and passing in applicable data.
  protected setStateFromStorage(
    storedState: StoredState<Partial<StoredConversationState>>,
  ) {
    this.storageVersion = storedState.version;

    if (storedState.data.status) {
      this.status$.next(storedState.data.status);
    }

    if (storedState.data.participants) {
      let updated = false;
      const participants = reduce(
        storedState.data.participants,
        (next, participant) => {
          const existing = this.participants[participant.id];
          if (isEqual(existing, participant)) {
            return next;
          }
          updated = true;
          next[participant.id] = participant;
          return next;
        },
        {...this.participants} as Record<string, ConversationParticipant>,
      );

      if (updated) {
        this.participants$.next(participants);
      }
    }

    if (storedState.data.messages) {
      const {messages} = this;
      let updated = false;
      storedState.data.messages.forEach((message) => {
        const existing = messages[message.id];
        const {timestamp} = existing || message;

        const nextMsg = {...message, timestamp};
        const existingMsg = {...existing, timestamp};

        // avoid unnecessary updates
        if (isEqual(nextMsg, existingMsg)) {
          return;
        }

        updated = true;

        messages[message.nonce || message.id] = nextMsg;
      });

      if (updated) {
        this.messages$.next(messages);
      }
    }

    if (storedState.data.log) {
      if (this.log.get().length !== storedState.data.log.length) {
        this.log._setWithoutUpdatingStorage(storedState.data.log);
      } else {
        const items = this.log.getUnsorted();
        let updated = false;
        storedState.data.log.forEach((item) => {
          const existing = items[item.id];
          const {timestamp} = existing || item;

          const nextItem = {...item, timestamp};
          const existingItem = {...existing, timestamp};

          // avoid unnecessary updates
          if (isEqual(nextItem, existingItem)) {
            return;
          }

          updated = true;

          items[item.id] = nextItem;
        });

        if (updated) {
          this.log._setWithoutUpdatingStorage(storedState.data.log);
        }
      }
    }

    if (storedState.data.context) {
      if (!isEqual(storedState.data.context, this.context)) {
        this.context$.next(storedState.data.context);
      }
    }
  }

  // Updates the conversation state if necessary, based on version and sync mode.
  protected updateFromStorage(
    storedState: StoredState<Partial<StoredConversationState>>,
  ) {
    try {
      if (storedState.version > this.storageVersion) {
        // Build up a storedState with the data we want to update
        const {version, timestamp} = storedState;
        const {context, status, participants, messages, log} = storedState.data;

        const data = {
          ...(this.syncStorageMode === 'sync' && {
            status,
            participants,
            messages,
            log,
          }),
          context,
        };

        const stateToStore: StoredState<Partial<StoredConversationState>> = {
          version,
          timestamp,
          data,
        };

        this.setStateFromStorage(stateToStore);
      }
    } catch (e) {}
  }

  protected markTabInteracted(): void {
    const {tabs} = this;

    const activeTab = tabs[0];
    if (activeTab?.id === this.tabId && activeTab.interacted) {
      return;
    }

    const otherTabs = tabs.filter((t) => t.id !== this.tabId);
    this.setContextValue('tabs', [
      {
        id: this.tabId,
        interacted: true,
      },
      ...otherTabs,
    ]);
  }

  protected markTabActive(): void {
    const {tabs} = this;

    if (tabs[0]?.id === this.tabId) {
      return;
    }

    const tab = tabs.find((t) => t.id === this.tabId) ?? {id: this.tabId};
    const otherTabs = tabs.filter((t) => t.id !== this.tabId);
    this.setContextValue('tabs', [tab, ...otherTabs]);
  }

  private registerTabFocusListeners(): void {
    if (this.tabEventsRegistered) {
      return;
    }

    const interactionListener = () => {
      this.markTabInteracted();
    };
    document.addEventListener('click', interactionListener);

    const activeListener = () => {
      if (document.visibilityState === 'visible') {
        this.markTabActive();
      }
    };
    document.addEventListener('visibilitychange', activeListener);

    // if the tab is about to close, remove it from the list of tabs
    const unloadListener = () => {
      const otherTabs = this.tabs.filter((t) => t.id !== this.tabId);
      this.setContextValue('tabs', otherTabs);
    };
    window.addEventListener('beforeunload', unloadListener);

    const existingTab = !!this.tabs.find((tab) => tab.id === this.tabId);
    if (!existingTab) {
      this.markTabActive();
    }

    this.tabEventsRegistered = true;
  }

  protected get metricTags(): Record<string, string> {
    return {clientType: this.client.type};
  }

  private incrementAndTrack(
    name: string,
    params: Record<string, string | number> = {},
  ): void {
    const metricParams = {clientType: this.client.type, ...params};
    this.client.metrics.increment(name, metricParams);
    this.client.analytics.track(name, metricParams);
  }

  private reportAndEmitConversationException(
    error: string,
    tags?: ErrorScope['tags'],
  ): void {
    const eventId = this.client.errors.reportError(error, {
      tags: {
        ...tags,
        assistant_error_screen: true,
      },
    });
    this.emitNewEvent({
      name: 'conversation-exception',
      conversationId: this.id,
      error,
      sentryEventId: eventId,
    });
  }
}
